import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { OfflineService, NetworkStatus, OfflineData } from '../../services/offline.service';
import { OfflineIndicatorComponent } from '../offline-indicator/offline-indicator.component';

interface DemoData {
  id: string;
  title: string;
  content: string;
  timestamp: Date;
  synced: boolean;
}

@Component({
  selector: 'app-offline-demo',
  standalone: true,
  imports: [CommonModule, FormsModule, OfflineIndicatorComponent],
  templateUrl: './offline-demo.component.html',
  styleUrl: './offline-demo.component.css'
})
export class OfflineDemoComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();
  
  networkStatus: NetworkStatus | null = null;
  syncInProgress = false;
  updateAvailable = false;
  
  // Demo data
  demoItems: DemoData[] = [];
  newItem = {
    title: '',
    content: ''
  };
  
  // Offline statistics
  offlineStats = {
    totalItems: 0,
    syncedItems: 0,
    pendingSync: 0,
    storageUsed: 0,
    lastSync: null as Date | null
  };

  // PWA features
  installPrompt: any = null;
  canInstall = false;
  isInstalled = false;

  constructor(private offlineService: OfflineService) {}

  ngOnInit(): void {
    // Subscribe to network status
    this.offlineService.networkStatus$
      .pipe(takeUntil(this.destroy$))
      .subscribe(status => {
        this.networkStatus = status;
        this.updateStats();
      });

    // Subscribe to sync status
    this.offlineService.syncInProgress$
      .pipe(takeUntil(this.destroy$))
      .subscribe(inProgress => {
        this.syncInProgress = inProgress;
        this.updateStats();
      });

    // Subscribe to update availability
    this.offlineService.updateAvailable$
      .pipe(takeUntil(this.destroy$))
      .subscribe(available => {
        this.updateAvailable = available;
      });

    // Load existing data
    this.loadDemoData();
    
    // Setup PWA install prompt
    this.setupPWAInstall();
    
    // Update stats periodically
    setInterval(() => this.updateStats(), 5000);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private setupPWAInstall(): void {
    // Check if already installed
    this.isInstalled = window.matchMedia('(display-mode: standalone)').matches ||
                      (window.navigator as any).standalone === true;

    // Listen for install prompt
    window.addEventListener('beforeinstallprompt', (e) => {
      e.preventDefault();
      this.installPrompt = e;
      this.canInstall = true;
    });

    // Listen for app installed
    window.addEventListener('appinstalled', () => {
      this.isInstalled = true;
      this.canInstall = false;
      this.installPrompt = null;
      console.log('PWA installed successfully');
    });
  }

  private loadDemoData(): void {
    const offlineData = this.offlineService.getOfflineData();
    this.demoItems = Object.entries(offlineData)
      .filter(([key, _]) => key.startsWith('demo-item-'))
      .map(([key, data]) => ({
        id: key.replace('demo-item-', ''),
        title: data.data.title,
        content: data.data.content,
        timestamp: new Date(data.timestamp),
        synced: data.synced
      }))
      .sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime());
  }

  private updateStats(): void {
    this.loadDemoData();
    
    this.offlineStats = {
      totalItems: this.demoItems.length,
      syncedItems: this.demoItems.filter(item => item.synced).length,
      pendingSync: this.offlineService.getSyncQueueCount(),
      storageUsed: this.offlineService.getOfflineDataCount(),
      lastSync: this.getLastSyncTime()
    };
  }

  private getLastSyncTime(): Date | null {
    const syncedItems = this.demoItems.filter(item => item.synced);
    if (syncedItems.length === 0) return null;
    
    return syncedItems.reduce((latest, item) => 
      item.timestamp > latest ? item.timestamp : latest, 
      syncedItems[0].timestamp
    );
  }

  // Demo actions
  addDemoItem(): void {
    if (!this.newItem.title.trim()) return;
    
    const id = Date.now().toString();
    const key = `demo-item-${id}`;
    const data = {
      title: this.newItem.title,
      content: this.newItem.content || 'No content provided',
      timestamp: new Date().toISOString()
    };
    
    // Store offline
    this.offlineService.storeOfflineData(key, data);
    
    // Add to sync queue if online
    if (this.networkStatus?.isOnline) {
      this.offlineService.addToSyncQueue({
        url: '/api/demo/items',
        method: 'POST',
        data: data,
        key: key
      });
    }
    
    // Reset form
    this.newItem = { title: '', content: '' };
    
    // Reload data
    this.loadDemoData();
    this.updateStats();
  }

  deleteDemoItem(item: DemoData): void {
    const key = `demo-item-${item.id}`;
    
    // Remove from offline storage
    this.offlineService.removeOfflineData(key);
    
    // Add delete operation to sync queue if online
    if (this.networkStatus?.isOnline) {
      this.offlineService.addToSyncQueue({
        url: `/api/demo/items/${item.id}`,
        method: 'DELETE',
        key: `delete-${key}`
      });
    }
    
    // Reload data
    this.loadDemoData();
    this.updateStats();
  }

  // Network simulation
  simulateOffline(): void {
    if ('serviceWorker' in navigator && 'controller' in navigator.serviceWorker) {
      console.log('Simulating offline mode - actual offline simulation requires service worker configuration');
      alert('In a real implementation, this would simulate offline mode via service worker.');
    } else {
      alert('Service worker not available. To test offline mode, use browser dev tools to go offline.');
    }
  }

  // Sync operations
  async manualSync(): Promise<void> {
    if (this.networkStatus?.isOnline && !this.syncInProgress) {
      await this.offlineService.syncOfflineData();
      this.updateStats();
    }
  }

  // Storage management
  clearOfflineData(): void {
    if (confirm('Are you sure you want to clear all offline data? This action cannot be undone.')) {
      this.offlineService.clearOfflineData();
      this.demoItems = [];
      this.updateStats();
    }
  }

  exportOfflineData(): void {
    const data = this.offlineService.exportOfflineData();
    const blob = new Blob([data], { type: 'application/json' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `rarr-offline-data-${Date.now()}.json`;
    link.click();
    window.URL.revokeObjectURL(url);
  }

  // PWA actions
  async installPWA(): Promise<void> {
    if (this.installPrompt) {
      try {
        const result = await this.installPrompt.prompt();
        console.log('Install prompt result:', result);
        
        if (result.outcome === 'accepted') {
          this.canInstall = false;
          this.installPrompt = null;
        }
      } catch (error) {
        console.error('Error installing PWA:', error);
      }
    }
  }

  async activateUpdate(): Promise<void> {
    if (this.updateAvailable) {
      try {
        await this.offlineService.activateUpdate();
      } catch (error) {
        console.error('Failed to activate update:', error);
        alert('Failed to activate update. Please refresh the page manually.');
      }
    }
  }

  // Cache management
  async clearCache(): Promise<void> {
    if (confirm('Are you sure you want to clear the application cache? This will remove all cached resources.')) {
      try {
        await this.offlineService.clearApplicationCache();
        alert('Application cache cleared successfully.');
      } catch (error) {
        console.error('Error clearing cache:', error);
        alert('Failed to clear application cache.');
      }
    }
  }

  // TrackBy function for demo items list
  trackByItemId(index: number, item: DemoData): string {
    return item.id;
  }

  // Utility methods
  getNetworkStatusClass(): string {
    if (!this.networkStatus) return 'text-muted';
    
    if (!this.networkStatus.isOnline) return 'text-danger';
    if (this.networkStatus.isSlowConnection) return 'text-warning';
    return 'text-success';
  }

  getStorageUsagePercentage(): number {
    // Simplified storage calculation for demo
    const maxItems = 100;
    return Math.min((this.offlineStats.totalItems / maxItems) * 100, 100);
  }

  formatTimestamp(date: Date): string {
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'short',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: true
    }).format(date);
  }

  formatRelativeTime(date: Date): string {
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const minutes = Math.floor(diff / (1000 * 60));
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);

    if (days > 0) return `${days}d ago`;
    if (hours > 0) return `${hours}h ago`;
    if (minutes > 0) return `${minutes}m ago`;
    return 'Just now';
  }

  // Feature detection
  getAvailableFeatures(): { name: string; available: boolean; description: string }[] {
    return [
      {
        name: 'Service Worker',
        available: 'serviceWorker' in navigator,
        description: 'Background sync and offline caching'
      },
      {
        name: 'Cache API',
        available: 'caches' in window,
        description: 'Manual cache management'
      },
      {
        name: 'IndexedDB',
        available: 'indexedDB' in window,
        description: 'Large offline data storage'
      },
      {
        name: 'Web App Manifest',
        available: true, // Always available in modern browsers
        description: 'App-like installation and behavior'
      },
      {
        name: 'Background Sync',
        available: 'serviceWorker' in navigator && 'sync' in window.ServiceWorkerRegistration.prototype,
        description: 'Automatic sync when online'
      },
      {
        name: 'Push Notifications',
        available: 'serviceWorker' in navigator && 'PushManager' in window,
        description: 'Real-time notifications'
      }
    ];
  }
}